<script lang="ts">
  import Search from '~icons/mdi/magnify';
  import Modal from '~/components/Modal.svelte';
  import { dictionary } from '~/algorithms/dictionary.svelte';
  import StaticWord from '~/components/StaticWord.svelte';
  import { isDaily } from '~/types';
  import { stringToWord } from '~/board/utils';
  import { ModalState } from '~/lib/nav.svelte';
  import { isPremium } from '~/lib/queries.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';

  const game = $derived(getGame().current);
  const dims = getDimensions();
  const premium = isPremium();
  const searchModal = new ModalState('search');

  type Props = {
    containerWidth: number;
    containerHeight: number;
  };
  const props: Props = $props();

  const searchHeight = 50;
  let suggestions = $state<string[]>([]);
  let inputRef = $state<HTMLInputElement>();
  let search = $state('');

  const handleFocus = () => {
    inputRef?.focus();
  };

  const handleOpenSearch = () => {
    if (!searchModal.open) {
      searchModal.push();
    }
  };

  let lastCalled = 0;
  const handleCloseSearch = async () => {
    if (Date.now() - lastCalled > 50) {
      lastCalled = Date.now();
      await searchModal.pop();
    }
  };

  $effect(() => {
    if (searchModal.open) {
      inputRef?.focus();
    }
  });

  $effect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        inputRef?.focus();
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  });

  $effect(() => {
    if (inputRef && !searchModal.open) {
      inputRef.value = '';
      inputRef.blur();
      suggestions = [];
    }
  });

  const handleInputChange = async (e: Event) => {
    if (!search.length) {
      suggestions = [];
      return;
    }

    const lenRange: [number, number] = isDaily(game)
      ? [1, Math.max(game.board.length, game.board[0].length)]
      : [4, 20];

    if (premium.data) {
      suggestions = await dictionary.searchRegex(search, lenRange);
    } else {
      suggestions = await dictionary.searchTrie(search, lenRange);
    }
  };
</script>

<div
  class="search-container"
  style="
    --translate-y: -{props.containerHeight - searchHeight}px;
    --translate-x: {(props.containerWidth - dims.board.width) / 2 - 48}px;
    --container-width: {props.containerWidth};
    --search-width: {dims.board.width}px;
  "
  role="button"
  tabindex="-1"
  onclick={handleFocus}
  onkeydown={() => null}
>
  <div class="search-inner">
    <div class="search-icon">
      <Search />
    </div>
    <input
      bind:this={inputRef}
      bind:value={search}
      onfocus={handleOpenSearch}
      onfocusout={handleCloseSearch}
      oninput={handleInputChange}
      id="word-lookup"
      class="search-input"
    />
  </div>
</div>
<Modal open={searchModal.open} alignTop hideContent onClose={handleCloseSearch}>
  {#snippet title()}
    <ul class="word-list">
      {#each suggestions as suggestion (suggestion)}
        <li>
          <StaticWord
            score={false}
            highlighted="green"
            word={{ ...stringToWord(suggestion), axis: 'col' }}
          />
        </li>
      {/each}
    </ul>
  {/snippet}
  {#snippet controls()}
    {#if !inputRef?.value?.length}
      Start typing a word
    {/if}
  {/snippet}
</Modal>

<style lang="scss">
  @use 'sass:color';
  @use '~/theme/include-media' as *;

  .search-container {
    cursor: pointer;
    transition:
      all 0.25s ease,
      z-index 0s ease;
    // position: absolute;
    font-size: 1em;
    // bottom: 0;
    // left: 0;
    z-index: 600;

    @include media('width<=tablet') {
      // font-size: 1.25em;
      .search-icon {
        left: 14px;
      }
    }

    &:focus-within {
      transform: translateY(var(--translate-y)) translateX(var(--translate-x));
      z-index: 1200;

      .search-icon {
        left: 1em;
      }
    }
  }
  .search-icon {
    transition: all 0.25s ease;
    z-index: 2;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 14px;
    display: flex;
    align-items: center;
    pointer-events: none;
    font-size: 20px;

    @include media('width<=tablet') {
      left: 16px;
      font-size: 18px;
    }
  }
  .search-inner {
    height: 44px;
    position: relative;
  }
  .search-input {
    height: 100%;
    $color: hsl(213, 56%, 71%);
    background-color: color.scale($color, $lightness: 80%);
    transition: all 0.5s ease;
    width: 0px;
    box-sizing: border-box;
    margin: 0;
    border-radius: 999px;
    outline: none;
    padding: 0;
    padding-left: 40px;
    border: 2px solid black;
    &:hover {
      background-color: color.scale($color, $lightness: 40%);
    }
    &:focus {
      background-color: white; //color.scale($color, $lightness: 20%);
      padding-left: 48px;
      width: var(--search-width);
      box-shadow: 0px 3px 6px #00000040;
    }
    :global(html.dark-mode) & {
      $color: #2f3640;
      color: white;
      background-color: $color;
      border-color: white;
    }
    @include media('width>=tablet') {
      font-size: 1.25em;
      padding: 0;
      padding-left: 40px;
    }
  }
  .word-list {
    overflow: hidden;
    list-style-type: none;
    display: grid;
    width: 100%;
    grid-auto-flow: row;
    padding: 0;
    margin: 0;
    li {
      padding: 0.5em;
    }
    :global(.tile .score) {
      display: none;
    }
  }
</style>
